import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import { InpatientRulesetType } from "types/InpatientRuleset";

export interface IInpatientRulesetFormData {
  providerId: string | null;
  payerId: number | null;
  name: string;
  drgCodes: string[];
  type: InpatientRulesetType;
  // Fixed Cost fields
  costToChargeRatio: number | null;
  fixedLossThresholdInCents: number | null;
  outlierPercent: number | null;
  // Pass Through fields
  thresholdInCents: number | null;
  chargeMultiplier: number | null;
  // Excessive LOS fields
  formula: string;
  // Transfer fields
  losFormula: "LOS" | "LOS_PLUS_1";
  dischargeStatusCodes: string[];
  // Stop Loss fields
  thresholdMultiplier: number | null;
  paymentPercent: number | null;
}

const RULESET_TYPES = [
  { label: "Fixed Cost", value: "FIXED_COST" as const },
  { label: "Pass Through", value: "PASS_THROUGH" as const },
  { label: "Excessive LOS", value: "EXCESSIVE_LOS" as const },
  { label: "Transfer", value: "TRANSFER" as const },
  { label: "Stop Loss", value: "STOP_LOSS" as const },
];

const EXCESSIVE_LOS_FORMULAS = [
  { label: "BCBST Day Outlier", value: "BCBST Day Outlier" },
];

const TRANSFER_LOS_FORMULAS = [
  { label: "Per Diem × LOS", value: "LOS" as const },
  { label: "Per Diem × (LOS + 1)", value: "LOS_PLUS_1" as const },
];

const DEFAULT_TRANSFER_DISCHARGE_CODES = [
  "02",
  "05",
  "62",
  "63",
  "65",
  "66",
  "70",
  "71",
  "72",
  "73",
  "74",
  "75",
  "76",
  "77",
  "78",
  "79",
  "80",
  "81",
  "82",
  "95",
];

export const INITIAL_FORM_DATA: IInpatientRulesetFormData = {
  providerId: null,
  payerId: null,
  name: "",
  drgCodes: [],
  type: "FIXED_COST",
  costToChargeRatio: null,
  fixedLossThresholdInCents: null,
  outlierPercent: 80,
  thresholdInCents: 0,
  chargeMultiplier: null,
  formula: "BCBST Day Outlier",
  losFormula: "LOS_PLUS_1",
  dischargeStatusCodes: [...DEFAULT_TRANSFER_DISCHARGE_CODES],
  thresholdMultiplier: null,
  paymentPercent: 75,
};

interface IInpatientRulesetFormProps {
  title: string;
  submitLabel: string;
  submitIcon: string;
  formData: IInpatientRulesetFormData;
  onChange: (data: IInpatientRulesetFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
}

export function InpatientRulesetForm({
  title,
  submitLabel,
  submitIcon,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
}: IInpatientRulesetFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    if (!payers?.data) return [];
    return payers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [payers]);

  const handleInputChange = (
    field: keyof IInpatientRulesetFormData,
    value: any
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const handleTypeChange = (newType: InpatientRulesetType) => {
    onChange({
      ...formData,
      type: newType,
      // Reset type-specific fields when switching
      costToChargeRatio:
        newType === "FIXED_COST" ? formData.costToChargeRatio : null,
      fixedLossThresholdInCents:
        newType === "FIXED_COST" ? formData.fixedLossThresholdInCents : null,
      outlierPercent: newType === "FIXED_COST" ? formData.outlierPercent : 80,
      thresholdInCents:
        newType === "PASS_THROUGH" ? formData.thresholdInCents : 0,
      chargeMultiplier:
        newType === "PASS_THROUGH" ? formData.chargeMultiplier : null,
      formula:
        newType === "EXCESSIVE_LOS"
          ? formData.formula || "BCBST Day Outlier"
          : "BCBST Day Outlier",
      losFormula: newType === "TRANSFER" ? formData.losFormula : "LOS_PLUS_1",
      dischargeStatusCodes:
        newType === "TRANSFER"
          ? formData.dischargeStatusCodes
          : [...DEFAULT_TRANSFER_DISCHARGE_CODES],
      thresholdMultiplier:
        newType === "STOP_LOSS" ? formData.thresholdMultiplier : null,
      paymentPercent: newType === "STOP_LOSS" ? formData.paymentPercent : 75,
    });
  };

  const commonFieldsValid =
    formData.providerId &&
    formData.payerId &&
    formData.name.trim() &&
    formData.drgCodes.length > 0;

  let typeFieldsValid = false;
  if (formData.type === "FIXED_COST") {
    typeFieldsValid =
      formData.costToChargeRatio != null &&
      formData.fixedLossThresholdInCents != null &&
      formData.outlierPercent != null;
  } else if (formData.type === "PASS_THROUGH") {
    typeFieldsValid =
      formData.thresholdInCents != null && formData.chargeMultiplier != null;
  } else if (formData.type === "EXCESSIVE_LOS") {
    typeFieldsValid = !!formData.formula;
  } else if (formData.type === "TRANSFER") {
    typeFieldsValid =
      !!formData.losFormula && formData.dischargeStatusCodes.length > 0;
  } else if (formData.type === "STOP_LOSS") {
    typeFieldsValid =
      formData.thresholdMultiplier != null && formData.paymentPercent != null;
  }

  const isValid = commonFieldsValid && typeFieldsValid;

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules?tab=4")}
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Card className="mb-4">
        <div className="grid">
          <div className="col-12 md:col-6 field">
            <label htmlFor="provider">Provider *</label>
            <Dropdown
              id="provider"
              className="w-full"
              value={formData.providerId}
              options={providerOptions}
              onChange={(e) => handleInputChange("providerId", e.value)}
              placeholder="Select Provider"
              filter
            />
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="payer">Payer *</label>
            <Dropdown
              id="payer"
              className="w-full"
              value={formData.payerId}
              options={payerOptions}
              onChange={(e) => handleInputChange("payerId", e.value)}
              placeholder="Select Payer"
              filter
            />
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="name">Name *</label>
            <InputText
              id="name"
              className="w-full"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="e.g., BCBS DRG 807 Fixed Cost"
            />
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="type">Type *</label>
            <Dropdown
              id="type"
              className="w-full"
              value={formData.type}
              options={RULESET_TYPES}
              onChange={(e) => handleTypeChange(e.value)}
            />
          </div>
          <div className="col-12 field">
            <label htmlFor="drgCodes">DRG Codes *</label>
            <Chips
              id="drgCodes"
              className="w-full"
              value={formData.drgCodes}
              onChange={(e) => handleInputChange("drgCodes", e.value || [])}
              placeholder="Type a DRG code and press Enter"
              separator=","
            />
            <small className="text-500">
              Press Enter or type comma to add each DRG code
            </small>
          </div>
        </div>
      </Card>

      {formData.type === "FIXED_COST" && (
        <Card title="Fixed Cost Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure high-cost outlier payment parameters for inpatient DRG
            cases. When a patient&apos;s estimated cost exceeds the fixed-loss
            threshold, an outlier payment is added to the expected amount.
          </div>
          <div className="grid">
            <div className="col-12 md:col-4 field">
              <label htmlFor="ccr">Cost-to-Charge Ratio (CCR) *</label>
              <InputNumber
                id="ccr"
                className="w-full"
                value={formData.costToChargeRatio}
                onValueChange={(e) =>
                  handleInputChange("costToChargeRatio", e.value)
                }
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={4}
                min={0}
                max={2}
                placeholder="e.g., 0.95"
              />
            </div>
            <div className="col-12 md:col-4 field">
              <label htmlFor="threshold">Fixed-Loss Threshold *</label>
              <InputNumber
                id="threshold"
                className="w-full"
                value={
                  formData.fixedLossThresholdInCents != null
                    ? formData.fixedLossThresholdInCents / 100
                    : undefined
                }
                onValueChange={(e) =>
                  handleInputChange(
                    "fixedLossThresholdInCents",
                    e.value != null ? Math.round(e.value * 100) : null
                  )
                }
                mode="currency"
                currency="USD"
                locale="en-US"
                min={0}
                placeholder="e.g., $200.00"
              />
            </div>
            <div className="col-12 md:col-4 field">
              <label htmlFor="outlierPct">Outlier Percentage *</label>
              <InputNumber
                id="outlierPct"
                className="w-full"
                value={formData.outlierPercent}
                onValueChange={(e) =>
                  handleInputChange("outlierPercent", e.value)
                }
                suffix="%"
                min={0}
                max={100}
                placeholder="e.g., 80"
              />
            </div>
          </div>
        </Card>
      )}

      {formData.type === "PASS_THROUGH" && (
        <Card title="Pass Through Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure pass-through calculation for inpatient claims with no fee
            schedule match. When total charges exceed the threshold, the
            expected amount is calculated as charges multiplied by the charge
            multiplier.
          </div>
          <div className="grid">
            <div className="col-12 md:col-6 field">
              <label htmlFor="ptThreshold">Charge Threshold *</label>
              <InputNumber
                id="ptThreshold"
                className="w-full"
                value={
                  formData.thresholdInCents != null
                    ? formData.thresholdInCents / 100
                    : undefined
                }
                onValueChange={(e) =>
                  handleInputChange(
                    "thresholdInCents",
                    e.value != null ? Math.round(e.value * 100) : null
                  )
                }
                mode="currency"
                currency="USD"
                locale="en-US"
                min={0}
                placeholder="e.g., $0.00"
              />
              <small className="text-500">
                Claims with charges above this threshold will use pass-through
                pricing
              </small>
            </div>
            <div className="col-12 md:col-6 field">
              <label htmlFor="chargeMultiplier">Charge Multiplier *</label>
              <InputNumber
                id="chargeMultiplier"
                className="w-full"
                value={formData.chargeMultiplier}
                onValueChange={(e) =>
                  handleInputChange("chargeMultiplier", e.value)
                }
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={4}
                min={0.01}
                max={10}
                placeholder="e.g., 1.25"
              />
              <small className="text-500">
                Expected amount = Total Charges x Multiplier
              </small>
            </div>
          </div>
        </Card>
      )}

      {formData.type === "EXCESSIVE_LOS" && (
        <Card title="Excessive LOS Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure day-outlier calculation for inpatient DRG cases with
            excessive length of stay. The formula params (ALOS, threshold,
            multiplier) come from the ProviderProcedure DRG fields.
          </div>
          <div className="grid">
            <div className="col-12 md:col-6 field">
              <label htmlFor="formula">Formula *</label>
              <Dropdown
                id="formula"
                className="w-full"
                value={formData.formula}
                options={EXCESSIVE_LOS_FORMULAS}
                onChange={(e) => handleInputChange("formula", e.value)}
                placeholder="Select Formula"
              />
            </div>
          </div>
        </Card>
      )}

      {formData.type === "TRANSFER" && (
        <Card title="Transfer Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure transfer per-diem pricing for inpatient DRG claims. When a
            claim&apos;s discharge status matches the configured codes, the
            expected amount is calculated as Per Diem × LOS (or LOS + 1).
          </div>
          <div className="grid">
            <div className="col-12 md:col-6 field">
              <label htmlFor="losFormula">LOS Formula *</label>
              <Dropdown
                id="losFormula"
                className="w-full"
                value={formData.losFormula}
                options={TRANSFER_LOS_FORMULAS}
                onChange={(e) => handleInputChange("losFormula", e.value)}
                placeholder="Select LOS Formula"
              />
            </div>
            <div className="col-12 field">
              <label htmlFor="dischargeStatusCodes">
                Discharge Status Codes *
              </label>
              <Chips
                id="dischargeStatusCodes"
                className="w-full"
                value={formData.dischargeStatusCodes}
                onChange={(e) =>
                  handleInputChange("dischargeStatusCodes", e.value || [])
                }
                placeholder="Type a status code and press Enter"
                separator=","
              />
              <small className="text-500">
                Claims with these discharge status codes will use transfer
                pricing
              </small>
            </div>
          </div>
        </Card>
      )}

      {formData.type === "STOP_LOSS" && (
        <Card title="Stop Loss Configuration" className="mb-4">
          <div className="text-600 mb-3">
            Configure stop loss payment for inpatient DRG cases. When billed
            charges exceed the threshold (a multiple of the DRG payment), the
            expected payment is replaced with a percentage of billed charges.
          </div>
          <div className="grid">
            <div className="col-12 md:col-6 field">
              <label htmlFor="thresholdMultiplier">
                Threshold (% of DRG Payment) *
              </label>
              <InputNumber
                id="thresholdMultiplier"
                className="w-full"
                value={formData.thresholdMultiplier}
                onValueChange={(e) =>
                  handleInputChange("thresholdMultiplier", e.value)
                }
                suffix="%"
                min={1}
                max={1000}
                placeholder="e.g., 200"
              />
              <small className="text-500">
                Stop loss triggers when charges exceed this % of the DRG payment
              </small>
            </div>
            <div className="col-12 md:col-6 field">
              <label htmlFor="paymentPercent">Payment (% of Charges) *</label>
              <InputNumber
                id="paymentPercent"
                className="w-full"
                value={formData.paymentPercent}
                onValueChange={(e) =>
                  handleInputChange("paymentPercent", e.value)
                }
                suffix="%"
                min={1}
                max={100}
                placeholder="e.g., 75"
              />
              <small className="text-500">
                Expected payment = this % of total billed charges
              </small>
            </div>
          </div>
        </Card>
      )}

      <div className="flex justify-content-end gap-2">
        <Button
          label="Cancel"
          className="p-button-outlined"
          onClick={() => navigate("/payer-rules?tab=4")}
        />
        <Button
          label={submitLabel}
          icon={submitIcon}
          disabled={!isValid || isSubmitting}
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
